import { useState, useEffect, useRef } from "react";
import { useForexData, type ForexItem } from "@/hooks/useForexData";
import { useScrollReveal, getRevealClasses } from "@/hooks/useScrollReveal";

function getDecimalPlaces(_pair: string) {
  return 2;
}

function getSpreadQuality(spread: number) {
  if (spread < 0.5) return "Tight";
  if (spread < 1.2) return "Avg";
  return "Wide";
}

function MiniSparkline({ data, trend, pair, width = 120, height = 36 }: { data: number[]; trend: string; pair: string; width?: number; height?: number }) {
  if (data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const points = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - ((v - min) / range) * (height - 4) - 2;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  const lastX = width;
  const lastY = height - ((data[data.length - 1] - min) / range) * (height - 4) - 2;
  const fillPath = `${points} L${lastX.toFixed(1)},${height} L0,${height} Z`;
  const color = trend === "up" ? "#10B981" : "#EF4444";
  const gradientId = `spark-fill-${pair.replace("/", "-")}`;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={fillPath} fill={`url(#${gradientId})`} />
      <path d={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SpreadGauge({ spread }: { spread: number }) {
  const quality = getSpreadQuality(spread);
  const color = quality === "Tight" ? "#10B981" : quality === "Avg" ? "#F59E0B" : "#EF4444";
  const width = quality === "Tight" ? 33 : quality === "Avg" ? 66 : 100;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1 bg-[#374151] rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${width}%`, backgroundColor: color }}
        />
      </div>
      <span className="text-[10px] font-medium whitespace-nowrap" style={{ color }}>
        {quality}
      </span>
    </div>
  );
}

export default function MarketDashboard() {
  const { ref, isVisible } = useScrollReveal(0.1);
  const { data, lastUpdate, isLoading, error, isLive } = useForexData();
  const [flashMap, setFlashMap] = useState<Record<string, string>>({});
  const prevPricesRef = useRef<Record<string, number>>({});

  // Flash detection when real data updates
  useEffect(() => {
    data.forEach((item) => {
      const prev = prevPricesRef.current[item.pair];
      if (prev !== undefined && item.price !== prev) {
        const dir = item.price > prev ? "up" : "down";
        setFlashMap((f) => ({ ...f, [item.pair]: dir }));
        setTimeout(() => {
          setFlashMap((f) => {
            const next = { ...f };
            delete next[item.pair];
            return next;
          });
        }, 800);
      }
      prevPricesRef.current[item.pair] = item.price;
    });
  }, [data]);

  const totalVolume = data.length;
  const mostActive = data.reduce((a, b) => (Math.abs(a.change) > Math.abs(b.change) ? a : b), data[0]);
  const bullishCount = data.filter((d) => d.trend === "up").length;
  const sentiment = bullishCount >= data.length / 2 ? "Bullish" : "Bearish";

  return (
    <section className="relative py-20 md:py-28 bg-[#111827] overflow-hidden" ref={ref}>
      {/* Ambient glows */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-[#10B981]/4 rounded-full blur-[140px]" />
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-[#7C3AED]/4 rounded-full blur-[120px]" />
      </div>

      <div className={`relative max-w-7xl mx-auto px-4 md:px-6 ${getRevealClasses(isVisible, "up")}`}>
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#10B981]/30 bg-[#10B981]/10 mb-5">
            <span className="relative flex h-2.5 w-2.5">
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${isLive ? "bg-[#10B981]" : isLoading ? "bg-[#F59E0B]" : "bg-[#EF4444]"}`} />
              <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${isLive ? "bg-[#10B981]" : isLoading ? "bg-[#F59E0B]" : "bg-[#EF4444]"}`} />
            </span>
            <span className="text-sm text-[#F8FAFC] font-medium">
              {isLive ? "Real-Time Market Data" : isLoading ? "Connecting to Markets..." : "Cached Market Data"}
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#F8FAFC] mb-4">
            Live Market <span className="text-gradient">Dashboard</span>
          </h2>
          <p className="text-base md:text-lg text-[#94A3B8] max-w-2xl mx-auto">
            Real-time spreads and pricing across major currency pairs &mdash; powered by live market data, updated every tick.
          </p>
          {error && (
            <p className="text-xs text-[#F59E0B] mt-3 font-mono">{error}</p>
          )}
        </div>

        {/* Market Overview Strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#1A2235] border border-[#2A3345] rounded-xl p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-[#7C3AED]/15 flex items-center justify-center shrink-0">
              <i className="ri-bar-chart-grouped-line text-[#7C3AED] text-lg" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-medium">Pairs Tracked</div>
              <div className="text-xl font-bold text-[#F8FAFC] font-mono">{totalVolume}</div>
            </div>
          </div>
          <div className="bg-[#1A2235] border border-[#2A3345] rounded-xl p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/15 flex items-center justify-center shrink-0">
              <i className="ri-fire-line text-[#F59E0B] text-lg" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-medium">Most Active</div>
              <div className="text-xl font-bold text-[#F8FAFC] font-mono truncate">{mostActive?.pair ?? "-"}</div>
            </div>
          </div>
          <div className="bg-[#1A2235] border border-[#2A3345] rounded-xl p-4 flex items-center gap-4">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${sentiment === "Bullish" ? "bg-[#10B981]/15" : "bg-[#EF4444]/15"}`}>
              <i className={`${sentiment === "Bullish" ? "ri-arrow-up-line text-[#10B981]" : "ri-arrow-down-line text-[#EF4444]"} text-lg`} />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-medium">Sentiment</div>
              <div className={`text-xl font-bold font-mono ${sentiment === "Bullish" ? "text-[#10B981]" : "text-[#EF4444]"}`}>{sentiment}</div>
            </div>
          </div>
          <div className="bg-[#1A2235] border border-[#2A3345] rounded-xl p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-[#10B981]/15 flex items-center justify-center shrink-0">
              <i className="ri-time-line text-[#10B981] text-lg" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-medium">Last Update</div>
              <div className="text-sm font-bold text-[#F8FAFC] font-mono">{lastUpdate.toLocaleTimeString()}</div>
            </div>
          </div>
        </div>

        {/* Main Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {data.map((item) => {
            const flashDir = flashMap[item.pair];
            const flashBorder =
              flashDir === "up"
                ? "border-[#10B981]/60 shadow-[0_0_18px_2px_rgba(16,185,129,0.12)]"
                : flashDir === "down"
                  ? "border-[#EF4444]/60 shadow-[0_0_18px_2px_rgba(239,68,68,0.12)]"
                  : "";

            const bid = item.price;
            const decimals = getDecimalPlaces(item.pair);
            const spreadPrice = item.spread * 0.0001;
            const ask = item.price + spreadPrice;

            return (
              <div
                key={item.pair}
                className={`relative bg-[#1A2235] border border-[#2A3345] rounded-xl p-5 transition-all duration-500 group hover:border-[#7C3AED]/50 hover:-translate-y-1 ${flashBorder}`}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2.5">
                    <span className="text-sm font-bold text-[#F8FAFC] tracking-wide">{item.pair}</span>
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                        item.trend === "up" ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#EF4444]/15 text-[#EF4444]"
                      }`}
                    >
                      {item.trend === "up" ? "Bullish" : "Bearish"}
                    </span>
                  </div>
                  <span
                    className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                      item.volume === "Very High" ? "bg-[#7C3AED]/15 text-[#7C3AED]" : item.volume === "High" ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#374151] text-[#94A3B8]"
                    }`}
                  >
                    {item.volume}
                  </span>
                </div>

                {/* Price area */}
                <div className="mb-1">
                  <div className={`text-2xl font-bold font-mono tracking-tight transition-colors duration-300 ${
                    flashDir === "up" ? "text-[#10B981]" : flashDir === "down" ? "text-[#EF4444]" : "text-[#F8FAFC]"
                  }`}>
                    {item.price.toFixed(decimals)}
                  </div>
                </div>

                {/* Bid / Ask row */}
                <div className="flex items-center gap-4 mb-4 text-[11px] font-mono">
                  <div className="flex-1">
                    <span className="text-[#64748B]">Bid </span>
                    <span className="text-[#94A3B8]">{bid.toFixed(decimals)}</span>
                  </div>
                  <div className="flex-1 text-right">
                    <span className="text-[#64748B]">Ask </span>
                    <span className="text-[#94A3B8]">{ask.toFixed(decimals)}</span>
                  </div>
                </div>

                {/* Sparkline */}
                <div className="mb-4 flex justify-center">
                  <MiniSparkline data={item.bars} trend={item.trend} pair={item.pair} width={130} height={40} />
                </div>

                {/* Change % */}
                <div className="flex items-center justify-between mb-4">
                  <div className={`text-sm font-semibold flex items-center gap-1 ${
                    item.change >= 0 ? "text-[#10B981]" : "text-[#EF4444]"
                  }`}>
                    <i className={item.change >= 0 ? "ri-arrow-up-line" : "ri-arrow-down-line"} />
                    {item.change >= 0 ? "+" : ""}{item.change}%
                  </div>
                  <span className="text-[10px] text-[#64748B]">24h</span>
                </div>

                {/* Spread gauge */}
                <div className="pt-3 border-t border-[#2A3345]">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] text-[#64748B]">Spread</span>
                    <span className="text-[11px] font-mono font-bold text-[#F8FAFC]">{item.spread} pips</span>
                  </div>
                  <SpreadGauge spread={item.spread} />
                </div>

                {/* Live indicator */}
                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="flex items-center gap-1 text-[10px] text-[#10B981]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
                    Live
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}